import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PAGE_CONFIG } from './Config';
import EmptyPage from '../../components/EmptyPage';
import { useTranslation } from 'react-i18next';
import PageIntro from '../../components/PageIntro';
import ContentCard from '../../components/ContentCard';
import TablePagination from '../../components/Pagination';
import tableStyles from '../../styles/tableStyles';
import api from '../../api';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

/** Material types already covered by their own top-level catalog pages. */
function isDedicatedCatalogMaterialType(name) {
    if (!name || typeof name !== 'string') return true;
    const n = name.toLowerCase().trim();
    return (
        n.includes('constitution') ||
        n === 'constitutions' ||
        n.includes('journal') ||
        n.includes('book') ||
        n === 'books' ||
        n.includes('report') ||
        n.includes('case') ||
        n === 'cases' ||
        n.includes('cutting') ||
        n.includes('audio') ||
        n.includes('video')
    );
}

const Other = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`other:${key}`), [t]);
    const pageConfig = useMemo(() => PAGE_CONFIG(tr), [tr]);

    const [selectedTypeId, setSelectedTypeId] = useState(null);
    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: ''
    });

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setParams((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (limit) => {
        if (limit < 1) return;
        setParams((prev) => ({ ...prev, limit }));
    };

    const { data: materialTypes } = useQuery({
        queryKey: ['materialTypes'],
        queryFn: () => api.materialTypes.getMaterialTypes(),
        select: (data) => data?.data?.data
    });

    const otherTypes = useMemo(() => {
        if (!materialTypes?.length) return [];
        return materialTypes.filter((mt) => mt?.name && !isDedicatedCatalogMaterialType(mt.name)).sort((a, b) => a.name.localeCompare(b.name));
    }, [materialTypes]);

    useEffect(() => {
        if (!otherTypes.length) {
            setSelectedTypeId(null);
            return;
        }
        setSelectedTypeId((prev) => {
            if (prev && otherTypes.some((t) => t.id === prev)) return prev;
            return otherTypes[0].id;
        });
    }, [otherTypes]);

    const handleSelectCategory = (id) => {
        setSelectedTypeId(id);
        setParams((prev) => ({ ...prev, page: 1 }));
    };

    const queryParams = useMemo(
        () => ({
            ...params,
            materialTypeId: selectedTypeId
        }),
        [params, selectedTypeId]
    );

    const { data: uploadsResponse, isLoading } = useQuery({
        queryKey: ['other', queryParams],
        queryFn: () => api.upload.getUploads(queryParams),
        enabled: !!selectedTypeId,
        select: (data) => data?.data
    });

    const items = uploadsResponse?.data;
    const pagination = {
        page: params.page,
        limit: params.limit,
        totalCount: uploadsResponse?.totalCount || 0
    };
    const totalPages = Math.ceil(pagination.totalCount / pagination.limit);

    return (
        <div>
            <PageIntro description={pageConfig.PAGE_DESCRIPTION} />

            {otherTypes.length > 0 ? (
                <>
                    <div className="mt-6 mb-4 overflow-x-auto">
                        <div className="flex flex-wrap gap-2 min-w-0 pb-1">
                            {otherTypes.map((mt) => {
                                const active = mt.id === selectedTypeId;
                                return (
                                    <button
                                        key={mt.id}
                                        type="button"
                                        onClick={() => handleSelectCategory(mt.id)}
                                        className={clsx(
                                            'whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                                            active
                                                ? 'bg-[#41A6D7] text-white dark:bg-[#256B8A]'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                                        )}
                                    >
                                        {mt.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="text-sm text-gray-500 ml-2 mt-4 dark:text-gray-400">
                        {tr('showing')} {items?.length ?? 0} {tr('of')} {pagination.totalCount}
                    </div>

                    {items && items.length > 0 ? (
                        <ContentCard data={items} />
                    ) : (
                        <EmptyPage
                            colSpan={12}
                            isLoading={isLoading}
                            icon={pageConfig.EMPTY_PAGE_ICON}
                            description={pageConfig.SEARCH_DESCRIPTION}
                            placeholder={pageConfig.SEARCH_PLACEHOLDER}
                            loadingMessage={pageConfig.SEARCH_LOADING_MESSAGE}
                        />
                    )}

                    {items && items.length > 0 && (
                        <TablePagination
                            totalPages={totalPages}
                            page={pagination.page}
                            limit={pagination.limit}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            tableStyles={tableStyles}
                        />
                    )}
                </>
            ) : (
                <div className="mt-8 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center text-gray-600 dark:text-gray-300">
                    {tr('noCategories')}
                </div>
            )}
        </div>
    );
};

export default Other;
